/**
 * v4 Settings — refined. Language + text size + about + log out.
 */

import { Link } from 'react-router-dom';
import { ArrowLeft, Languages, Type, Info, LogOut, CheckCircle2, Sun, Moon, Palette, Droplet } from 'lucide-react';
import { useV4, LANGS, FontSize, Lang, ThemeMode, Accent, ACCENTS } from './i18n';
import { cn } from '@/lib/utils';

export default function SettingsPageV4() {
  const { t, settings, setLang, setFontSize, setAccent, dir, themeMode, setThemeMode } = useV4();

  return (
    <div className="space-y-5 py-4 pb-12">
      <div className="flex items-center gap-2">
        <Link
          to="/client-pwa-v4"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground hover:border-muted-foreground/40"
          aria-label="Back"
        >
          <ArrowLeft className={`h-4 w-4 ${dir === 'rtl' ? 'rotate-180' : ''}`} />
        </Link>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t('settings')}</p>
      </div>

      <Section icon={Languages} title={t('language')}>
        <div className="grid grid-cols-2 gap-2 p-3">
          {LANGS.map(l => {
            const isActive = settings.lang === l.code;
            return (
              <button
                key={l.code}
                onClick={() => setLang(l.code as Lang)}
                dir={l.dir}
                style={{ fontFamily: l.font }}
                className={cn(
                  'flex flex-col items-start gap-0.5 rounded-xl border p-3 transition-all',
                  isActive
                    ? 'border-[var(--v4-accent)] bg-[var(--v4-accent-tint)] shadow-sm'
                    : 'border-border bg-card hover:border-muted-foreground/40'
                )}
              >
                <div className="flex w-full items-center justify-between">
                  <span className="text-base font-semibold">{l.native}</span>
                  {isActive && <CheckCircle2 className="h-4 w-4 text-[var(--v4-accent-text)]" />}
                </div>
                <span className="text-[10px] text-muted-foreground">{l.english}</span>
              </button>
            );
          })}
        </div>
      </Section>

      <Section icon={Palette} title={t('appearance')}>
        <div className="grid grid-cols-2 gap-2 p-3">
          {([
            { v: 'light', l: t('theme_light'), icon: Sun },
            { v: 'dark',  l: t('theme_dark'),  icon: Moon },
          ] as { v: ThemeMode; l: string; icon: any }[]).map(opt => {
            const isActive = themeMode === opt.v;
            const Icon = opt.icon;
            return (
              <button
                key={opt.v}
                onClick={() => setThemeMode(opt.v)}
                className={cn(
                  'flex items-center gap-2 rounded-xl border p-3 transition-all',
                  isActive
                    ? 'border-[var(--v4-accent)] bg-[var(--v4-accent-tint)] shadow-sm'
                    : 'border-border bg-card hover:border-muted-foreground/40'
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="text-sm font-semibold flex-1 text-start">{opt.l}</span>
                {isActive && <CheckCircle2 className="h-4 w-4 text-[var(--v4-accent-text)]" />}
              </button>
            );
          })}
        </div>
      </Section>

      <Section icon={Droplet} title="Accent colour">
        <div className="grid grid-cols-4 gap-2 p-3">
          {(Object.entries(ACCENTS) as [Accent, typeof ACCENTS[Accent]][]).map(([key, def]) => {
            const isActive = settings.accent === key;
            return (
              <button
                key={key}
                onClick={() => setAccent(key)}
                title={def.name}
                aria-label={def.name}
                className={cn(
                  'group relative flex flex-col items-center gap-1 rounded-md border p-2 transition-all',
                  isActive ? 'border-foreground shadow-sm' : 'border-border hover:border-muted-foreground/40'
                )}
              >
                <span
                  className="relative flex h-9 w-9 items-center justify-center rounded-full"
                  style={{ backgroundColor: def.primary }}
                >
                  {isActive && <CheckCircle2 className="h-4 w-4 text-white" strokeWidth={3} />}
                </span>
                <span className="text-[10px] font-semibold text-muted-foreground">{def.name}</span>
              </button>
            );
          })}
        </div>
      </Section>

      <Section icon={Type} title={t('text_size')}>
        <div className="grid grid-cols-3 gap-2 p-3">
          {([
            { v: 'normal', l: t('text_normal'), size: 'text-sm' },
            { v: 'large',  l: t('text_large'),  size: 'text-base' },
            { v: 'xl',     l: t('text_xl'),     size: 'text-lg' },
          ] as { v: FontSize; l: string; size: string }[]).map(opt => {
            const isActive = settings.fontSize === opt.v;
            return (
              <button
                key={opt.v}
                onClick={() => setFontSize(opt.v)}
                className={cn(
                  'flex flex-col items-center gap-1 rounded-xl border px-3 py-3 transition-all',
                  isActive
                    ? 'border-[var(--v4-accent)] bg-[var(--v4-accent-tint)]'
                    : 'border-border bg-card hover:border-muted-foreground/40'
                )}
              >
                <span className={cn('font-bold text-foreground', opt.size)}>Aa</span>
                <span className="text-[10px] text-muted-foreground">{opt.l}</span>
              </button>
            );
          })}
        </div>
      </Section>

      <Section icon={Info} title={t('about')}>
        <div className="divide-y divide-border/60">
          <Row label={t('app_name')} value="v4.0" />
          <Row label={t('powered_by')} value="RoniDoc" />
          <Row label={t('your_accountant')} value="Marcus Bell" />
          <Row label="Firm" value={t('firm_name')} />
        </div>
      </Section>

      <button
        onClick={() => { /* signOut */ }}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-rose-200 dark:border-rose-900/60 bg-rose-50/60 dark:bg-rose-950/30 p-3.5 text-sm font-semibold text-rose-700 dark:text-rose-300 transition-colors hover:border-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/50"
      >
        <LogOut className="h-4 w-4" />
        {t('log_out')}
      </button>
    </div>
  );
}

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="mb-2 flex items-center gap-1.5 px-1 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
        <Icon className="h-3 w-3" />
        {title}
      </h2>
      <div className="rounded-2xl border border-border bg-card">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground">{value}</span>
    </div>
  );
}
